import { useSyncExternalStore } from 'react';

const APP_MENTIONS_STORAGE_KEY = 'littlebird:app-mentions:recents';
const APP_MENTIONS_LIMIT = 50;

export type RememberedAppMention = Readonly<{
  name: string;
  bundleId: string | null;
  iconUrl: string | null;
}>;

let snapshot: readonly RememberedAppMention[] = readAppMentions();
const listeners = new Set<() => void>();

function isRememberedAppMention(value: unknown): value is RememberedAppMention {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.name === 'string' &&
    (typeof entry.bundleId === 'string' || entry.bundleId === null) &&
    (typeof entry.iconUrl === 'string' || entry.iconUrl === null)
  );
}

function immutableAppMentions(
  entries: readonly RememberedAppMention[]
): readonly RememberedAppMention[] {
  const names = new Set<string>();
  return Object.freeze(
    entries
      .flatMap((entry) => {
        if (names.has(entry.name)) return [];
        names.add(entry.name);
        return [Object.freeze({ ...entry })];
      })
      .slice(0, APP_MENTIONS_LIMIT)
  );
}

function readAppMentions(): readonly RememberedAppMention[] {
  try {
    const stored: unknown = JSON.parse(
      localStorage.getItem(APP_MENTIONS_STORAGE_KEY) ?? '[]'
    );
    return Array.isArray(stored)
      ? immutableAppMentions(stored.filter(isRememberedAppMention))
      : [];
  } catch {
    return [];
  }
}

const getSnapshot = () => snapshot;

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export function rememberAppMention(app: RememberedAppMention): void {
  snapshot = immutableAppMentions([
    app,
    ...snapshot.filter(({ name }) => name !== app.name),
  ]);
  try {
    localStorage.setItem(APP_MENTIONS_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Keep the in-memory recents available when storage cannot be written
  }
  listeners.forEach((listener) => listener());
}

export function useRememberedAppMentions(): readonly RememberedAppMention[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function findRememberedAppMention(
  name: string
): RememberedAppMention | undefined {
  return snapshot.find((entry) => entry.name === name);
}
